import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import {
  ATRIUM_TYPES,
  CBORD_TYPES,
  ITC_TYPES,
  MEALPLAN_TYPES,
  TENDER_TYPES,
  TOUCHNET_TYPES,
  TRANSACT_TYPES,
} from '../../../components';

export default () => {
  // Set Localization
  const { t } = useI18n();

  // Set numberTransform
  const numberTransform = (value: any) => (isNaN(value) ? undefined : value);

  // Regular expression for validating IP addresses
  const ipRegex =
    /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;

  const schema = yup.object({
    id: yup
      .string()
      .nullable()
      .when('type', {
        is: (type: string) => ITC_TYPES.includes(type) || TOUCHNET_TYPES.includes(type),
        then: (schema) => schema.required(t(`errorMessages.required`)),
        otherwise: (schema) => schema.optional(),
      }),
    name: yup
      .string()
      .nullable()
      .test({
        name: 'test-empty-string',
        test(value, ctx) {
          if (value?.startsWith(' ')) {
            return ctx.createError({ message: t(`errorMessages.startSpace`) });
          }
          return true;
        },
      })
      .required(t(`errorMessages.required`))
      .max(64, t(`errorMessages.max`, { count: 64 })),
    type: yup.string().nullable().required(t(`errorMessages.required`)),

    keystore: yup
      .object()
      .when('type', {
        // Keystore schema for ITC types
        is: (type: string) => ITC_TYPES.includes(type),
        then: yup.object({
          certificate: yup.object({
            encoded_file: yup.string().nullable(),
            name: yup.string().nullable().required(t(`errorMessages.required`)),
            password: yup.string().nullable().required(t(`errorMessages.required`)),
          }),
          device_id: yup
            .number()
            .transform(numberTransform)
            .nullable()
            .required(t(`errorMessages.required`)),
          endpoint: yup
            .string()
            .url(t(`errorMessages.invalidURL`))
            .nullable()
            .required(t(`errorMessages.required`)),
          vendor_id: yup.string().nullable().required(t(`errorMessages.required`)),
          webview: yup
            .string()
            .url(t(`errorMessages.invalidURL`))
            .nullable()
            .required(t(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for Atrium direct
        is: (type: string) => type === MEALPLAN_TYPES.ATRIUM,
        then: yup.object({
          API_URL: yup.string().nullable().required(t(`errorMessages.required`)),
          key: yup.string().nullable().required(t(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for Atrium SSO
        is: (type: string) => type === MEALPLAN_TYPES.ATRIUM_SSO,
        then: yup.object({
          API_URL: yup.string().nullable().required(t(`errorMessages.required`)),
          key: yup.string().nullable().required(t(`errorMessages.required`)),
          client: yup.string().nullable().required(t(`errorMessages.required`)),
          webview: yup.string().nullable().required(t(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for TouchNet types
        is: (type: string) => TOUCHNET_TYPES.includes(type),
        then: yup.object({
          operator_id: yup.string().nullable().required(t(`errorMessages.required`)),
          operator_password: yup.string().nullable().required(t(`errorMessages.required`)),
          api_url: yup
            .string()
            .url(t(`errorMessages.invalidURL`))
            .nullable()
            .required(t(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for Blackboard types
        is: (type: string) => TRANSACT_TYPES.includes(type),
        then: yup.object({
          host: yup
            .string()
            .nullable()
            .required(t(`errorMessages.required`))
            .matches(ipRegex, t('errorMessages.invalidIP')),
          aes_key: yup.string().nullable().required(t(`errorMessages.required`)),
          port: yup
            .number()
            .transform(numberTransform)
            .nullable()
            .required(t(`errorMessages.required`)),
          vendor_number: yup
            .number()
            .transform(numberTransform)
            .nullable()
            .required(t(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for CBORD Web
        is: (type: string) => type === MEALPLAN_TYPES.CBORD,
        then: yup.object({
          auth_url: yup.string().nullable().required(t(`errorMessages.required`)),
          uuid: yup.string().nullable().required(t(`errorMessages.required`)),
        }),
      })
      .when('type', {
        // Keystore schema for CBORD Direct
        is: (type: string) => type === MEALPLAN_TYPES.CBORD_DIRECT,
        then: yup.object({
          uuid: yup.string().nullable().required(t(`errorMessages.required`)),
        }),
      }),
    // Config for atrium
    config: yup.object().when('type', {
      is: (type: string) => ATRIUM_TYPES.includes(type),
      then: yup.object({
        terminal_id: yup.array().of(yup.string().optional().nullable()).optional().nullable(),
      }),
    }),
    // Notes for all types except ITC
    note: yup
      .object()
      .nullable()
      .when('type', {
        is: (type: string) => !ITC_TYPES.includes(type),
        then: yup.object({
          en: yup.string().nullable().optional(),
          fr: yup.string().nullable().optional(),
        }),
      }),
    terminal_id: yup
      .array()
      .nullable()
      .when('type', {
        // Terminal id for touchnet
        is: (type: string) => TOUCHNET_TYPES.includes(type),
        then: yup
          .array()
          .of(yup.string().nullable().required(t(`errorMessages.required`)))
          .min(1, t(`errorMessages.minValue`, { minValue: 1 }))
          .required(t(`errorMessages.required`)),
      })
      .when('type', {
        // Terminal ids for transact
        is: (type: string) => TRANSACT_TYPES.includes(type),
        then: yup
          .array()
          .of(
            yup
              .number()
              .nullable()
              .typeError(t(`errorMessages.invalidNumber`, { type: 'number' }))
              .required(t(`errorMessages.required`)),
          )
          .min(1, t(`errorMessages.minValue`, { minValue: 1 }))
          .required(t(`errorMessages.required`)),
      })
      .when('type', {
        // Terminal ids for CBORD
        is: (type: string) => CBORD_TYPES.includes(type),
        then: yup.array().of(yup.string().nullable().optional()).nullable().optional(),
      }),
    tax_exempt: yup
      .boolean()
      .nullable()
      .when('type', {
        // Tax exempt for touchnet
        is: (type: string) => TOUCHNET_TYPES.includes(type),
        then: yup.boolean().nullable().required(t(`errorMessages.required`)),
      }),
    tenders: yup
      .array()
      .nullable()
      .when('type', {
        is: (type: string) => ITC_TYPES.includes(type),
        then: yup
          .array()
          .nullable()
          .of(
            yup.object({
              id: yup.string().nullable().required(t(`errorMessages.required`)),
              tax_exempt: yup.boolean().when('type', {
                is: (type: string) => TENDER_TYPES.MEAL_EXCHANGE !== type,
                then: (schema) => schema.nullable().required(t(`errorMessages.required`)),
                otherwise: (schema) => schema.nullable(),
              }),
              type: yup.string().nullable().required(t(`errorMessages.required`)),
              tender_number: yup.string().nullable(),
            }),
          ),
      })
      .when('type', {
        is: (type: string) =>
          ATRIUM_TYPES.includes(type) ||
          TRANSACT_TYPES.includes(type) ||
          CBORD_TYPES.includes(type),
        then: yup
          .array()
          .nullable()
          .of(
            yup.object({
              id: yup.string().nullable().required(t(`errorMessages.required`)),
              name: yup.string().nullable().required(t(`errorMessages.required`)),
              max_swipes_per_transaction: yup
                .number()
                .transform(numberTransform)
                .nullable()
                .when('type', {
                  is: (type: string) => TENDER_TYPES.MEAL_EQUIVALENT === type,
                  then: (schema) => schema.nullable().required(t(`errorMessages.required`)),
                  otherwise: (schema) => schema.nullable(),
                }),
              tax_exempt: yup.boolean().when('type', {
                is: (type: string) => TENDER_TYPES.MEAL_EXCHANGE !== type,
                then: (schema) => schema.nullable().required(t(`errorMessages.required`)),
                otherwise: (schema) => schema.nullable(),
              }),
              type: yup.string().nullable().required(t(`errorMessages.required`)),
            }),
          )
          .min(1, t(`errorMessages.minValue`, { minValue: 1 }))
          .required(t(`errorMessages.required`)),
      }),
    valid_email_domains: yup.array().when('type', {
      // Transact and Atrium valid domains
      is: (type: string) => TRANSACT_TYPES.includes(type) || ATRIUM_TYPES.includes(type),
      then: yup.array().nullable().optional().of(yup.string().nullable().optional()),
      otherwise: yup.array().nullable().optional(),
    }),
  });

  const tenderSetupSchema = yup.object({
    id: yup.string().nullable().required(t(`errorMessages.required`)),
    max_swipes_per_transaction: yup
      .number()
      .transform(numberTransform)
      .nullable()
      .required(t(`errorMessages.required`)),
    tax_exempt: yup.boolean().nullable().required(t(`errorMessages.required`)),
    rates: yup
      .array()
      .of(
        yup.object({
          amount: yup
            .number()
            .transform(numberTransform)
            .nullable()
            .moreThan(0, t(`errorMessages.greaterThan`, { greaterThan: 0 }))
            .required(t(`errorMessages.required`)),
          startTime: yup.date().nullable().required(t(`errorMessages.required`)),
          endTime: yup.date().nullable().required(t(`errorMessages.required`)),
        }),
      )
      .nullable()
      .min(1)
      .required(t(`errorMessages.required`)),
  });

  // Custom domain validation function
  const validDomain = (domain: string): boolean => {
    // Regular expression to validate domains (e.g., "example.com", "test.io")
    const domainRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(domain);
  };

  const domainsSchema = yup.object({
    domain: yup
      .string()
      .required(t(`errorMessages.required`)) // Ensure it's required
      .test('is-valid-domain', t(`errorMessages.invalidDomain`), (value) => {
        return validDomain(value || ''); // Validate domain if value exists
      }),
  });

  return { schema, tenderSetupSchema, domainsSchema };
};
